import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Plus, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SI, matchClass, siErr, siPost, useSI } from "@/lib/sourcing";

const tone = (s) => (s >= 80 ? "text-emerald-300 border-emerald-500/40" : s >= 60 ? "text-[#F3C649] border-[#D4AF37]/40" : "text-zinc-400 border-[#27272A]");

// §75 Top 5 open projects this internal person fits, with match score and one-tap add.
export const BestFit = ({ id, readOnly }) => {
  const r = useSI(`${SI}/talent/${id}/best-fit?limit=5`);
  const [busy, setBusy] = useState("");
  const [added, setAdded] = useState([]);
  const add = async (p) => {
    setBusy(p.project_id);
    try { await siPost("/talent/add-to-project", { project_id: p.project_id, candidate_ids: [id] }); setAdded([...added, p.project_id]); toast.success(`Added to ${p.name}`); }
    catch (e) { toast.error(siErr(e)); } finally { setBusy(""); }
  };
  const items = (r.data?.items || []).slice(0, 5);
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#0f0f12] p-3" data-testid="tp-bestfit">
      <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500"><Target className="h-3.5 w-3.5 text-[#D4AF37]" />Best fit open projects</p>
      {r.missing ? <p className="text-xs text-zinc-500" data-testid="tp-bestfit-missing">Project matching isn't live on the backend yet (spec §75).</p>
        : r.loading && !r.data ? <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />
          : !items.length ? <p className="text-xs text-zinc-500" data-testid="tp-bestfit-empty">No open projects to match against yet.</p>
            : items.map((p) => {
              const s = Math.round(p.overall_score ?? 0); const inP = p.in_project || added.includes(p.project_id);
              return (
                <div key={p.project_id} className="flex items-center gap-3 border-t border-[#1f1f23] py-2 first:border-t-0" data-testid={`tp-fit-${p.project_id}`}>
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-mono text-sm ${tone(s)}`} data-testid={`tp-fit-${p.project_id}-score`}>{s}</span>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm text-zinc-100">{p.name}</p>
                    <p className="truncate text-[11px] text-zinc-500">{[p.job_title, matchClass(s)].filter(Boolean).join(" · ")}{p.reasons?.length ? ` — ${p.reasons.slice(0, 2).join("; ")}` : ""}</p></div>
                  {inP ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`tp-fit-${p.project_id}-in`}><Check className="h-3.5 w-3.5" />In project</span>
                    : !readOnly && <Button size="sm" variant="outline" disabled={busy === p.project_id} onClick={() => add(p)} className="h-7 border-[#27272A] text-xs text-zinc-200" data-testid={`tp-fit-${p.project_id}-add`}>{busy === p.project_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}Add</Button>}
                </div>
              );
            })}
    </div>
  );
};
